import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ImageIcon, AlertCircle, ChevronLeft, ChevronRight, Download, Share2,
  ArrowUpDown, Search, X, Link, MessageCircle, ExternalLink, Grid3X3,
  LayoutList, Calendar, Eye, Video, Play
} from 'lucide-react';

const PhotoGallery = () => {
  const [media, setMedia] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortNewest, setSortNewest] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [shareMenu, setShareMenu] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || 'all';
  const touchStart = useRef(null);
  const shareRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get('/api/photos'),
      axios.get('/api/categories'),
    ]).then(([mediaRes, catRes]) => {
      setMedia(mediaRes.data);
      setCategories(catRes.data);
    }).catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (shareMenu === null) return;
    const handler = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShareMenu(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [shareMenu]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const filtered = useMemo(() => {
    return media
      .filter((m) => {
        if (activeCategory !== 'all' && m.categoryId !== activeCategory) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (m.filename || '').toLowerCase().includes(q) || (m.originalName || '').toLowerCase().includes(q);
      })
      .sort((a, b) => sortNewest ? new Date(b.uploadedAt) - new Date(a.uploadedAt) : new Date(a.uploadedAt) - new Date(b.uploadedAt));
  }, [media, search, sortNewest, activeCategory]);

  const catMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.id] = c; });
    return map;
  }, [categories]);

  const openLightbox = useCallback((index) => { setShareMenu(null); setLightbox({ open: true, index }); }, []);
  const closeLightbox = useCallback(() => setLightbox({ open: false, index: 0 }), []);
  const goNext = useCallback(() => setLightbox((prev) => ({ open: true, index: (prev.index + 1) % filtered.length })), [filtered.length]);
  const goPrev = useCallback(() => setLightbox((prev) => ({ open: true, index: (prev.index - 1 + filtered.length) % filtered.length })), [filtered.length]);

  useEffect(() => {
    if (!lightbox.open) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox.open, closeLightbox, goNext, goPrev]);

  useEffect(() => {
    document.body.style.overflow = lightbox.open ? 'hidden' : '';
    document.body.classList.toggle('lightbox-active', lightbox.open);
    window.dispatchEvent(new CustomEvent('lightbox', { detail: { open: lightbox.open } }));
    return () => { document.body.style.overflow = ''; document.body.classList.remove('lightbox-active'); };
  }, [lightbox.open]);

  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? goNext() : goPrev();
    touchStart.current = null;
  };

  const handleDownload = (item) => {
    const a = document.createElement('a');
    a.href = `/api/photos/${item.id}/download`;
    a.download = item.originalName || item.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Download started');
  };

  const getShareUrl = (item) => window.location.origin + item.url;

  const handleShare = async (item) => {
    const url = getShareUrl(item);
    const text = `Check out this: ${item.originalName || item.filename}`;
    if (navigator.share) {
      try { await navigator.share({ title: item.originalName || item.filename, text, url }); } catch {}
      return;
    }
    setShareMenu((prev) => (prev === item.id ? null : item.id));
  };

  const shareTo = (platform, item) => {
    const url = getShareUrl(item);
    const text = encodeURIComponent(`Check out this: ${item.originalName || item.filename}`);
    const encodedUrl = encodeURIComponent(url);
    const links = {
      whatsapp: `https://wa.me/?text=${text}%20${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    };
    if (links[platform]) window.open(links[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
    setShareMenu(null);
  };

  const copyLink = async (item) => {
    try { await navigator.clipboard.writeText(getShareUrl(item)); toast.success('Link copied'); }
    catch { toast.error('Failed to copy'); }
    setShareMenu(null);
  };

  const formatBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
    return (b / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  const totalSize = useMemo(() => media.reduce((acc, m) => acc + (m.size || 0), 0), [media]);
  const photoCount = media.filter((m) => m.type === 'image').length;
  const videoCount = media.filter((m) => m.type === 'video').length;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="skeleton h-9 w-48 mb-2" />
        <div className="skeleton h-4 w-32 mb-6" />
        <div className="flex gap-3 mb-6">
          <div className="skeleton h-10 w-64" />
          <div className="skeleton h-10 w-24" />
        </div>
        <div className="flex gap-2 mb-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-8 w-20 rounded-full" />)}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square skeleton" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-flex flex-col items-center gap-3">
          <div className="w-14 h-14 bg-destructive/10 rounded-2xl flex items-center justify-center border border-destructive/20">
            <AlertCircle size={24} className="text-destructive" />
          </div>
          <p className="text-sm font-bold text-foreground">{error}</p>
          <button onClick={() => { setError(''); setLoading(true); window.location.reload(); }}
            className="btn-primary btn-sm mt-1">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in pt-24">
      {/* ─── Hero Section ─── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary/[0.04] via-background to-primary-container/10 border-b border-border/50">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 right-[20%] w-20 h-20 bg-primary-container/15 rounded-full blur-2xl float-gentle" />
          <div className="absolute bottom-4 left-[15%] w-16 h-16 bg-secondary-container/15 rounded-full blur-2xl float-gentle-delayed" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 relative z-10">
          <div className="max-w-2xl">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground">Album</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-2 leading-relaxed font-medium">Browse and download photos & videos.</p>
            {media.length > 0 && (
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                {photoCount > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                    <div className="w-6 h-6 stat-icon-rose rounded-full flex items-center justify-center">
                      <ImageIcon size={12} />
                    </div>
                    <span className="font-bold text-foreground">{photoCount}</span> photo{photoCount !== 1 ? 's' : ''}
                  </div>
                )}
                {videoCount > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                    <div className="w-6 h-6 stat-icon-purple rounded-full flex items-center justify-center">
                      <Video size={12} />
                    </div>
                    <span className="font-bold text-foreground">{videoCount}</span> video{videoCount !== 1 ? 's' : ''}
                  </div>
                )}
                <div className="text-xs text-muted-foreground font-medium">{formatBytes(totalSize)} total</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Toolbar ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] sm:max-w-xs">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" aria-hidden="true" />
            <input ref={searchRef} type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 bg-card border border-border/60 rounded-2xl text-sm font-medium placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary/30 transition-all duration-200"
              aria-label="Search media" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors cursor-pointer" aria-label="Clear">
                <X size={14} />
              </button>
            )}
          </div>
          <button onClick={() => setSortNewest(!sortNewest)}
            className="flex items-center gap-1.5 px-3 py-2.5 bg-card border border-border/60 rounded-2xl text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/20 transition-all duration-200 cursor-pointer">
            <ArrowUpDown size={14} />
            <span className="hidden sm:inline">{sortNewest ? 'Newest' : 'Oldest'}</span>
          </button>
          <div className="hidden sm:flex items-center bg-card border border-border/60 rounded-2xl overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={`p-2.5 transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`} aria-label="Grid"><Grid3X3 size={15} /></button>
            <button onClick={() => setViewMode('large')} className={`p-2.5 transition-colors cursor-pointer ${viewMode === 'large' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`} aria-label="Large"><LayoutList size={15} /></button>
          </div>
          <span className="text-xs text-muted-foreground font-bold tabular-nums">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Category tabs */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 mt-3 overflow-x-auto scrollbar-thin pb-1">
            <button onClick={() => setSearchParams({})}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                activeCategory === 'all' ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border/60 text-muted-foreground hover:text-foreground hover:border-primary/20'
              }`}>
              All
            </button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setSearchParams({ cat: c.id })}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeCategory === c.id ? 'text-white shadow-sm' : 'bg-card border border-border/60 text-muted-foreground hover:text-foreground hover:border-primary/20'
                }`}
                style={activeCategory === c.id ? { backgroundColor: c.color } : {}}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color, opacity: activeCategory === c.id ? 0 : 1 }} />
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ─── Media Grid ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {media.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-20 h-20 bg-primary-container/20 rounded-3xl flex items-center justify-center mx-auto mb-5 border border-primary-container/30 shadow-bubble">
              <ImageIcon size={32} className="text-primary/50" />
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground mb-1.5">No media yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto leading-relaxed font-medium">Upload your first photo or video from the dashboard.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search size={24} className="text-muted-foreground/60" />
            </div>
            <p className="text-muted-foreground text-sm mb-1 font-medium">No results</p>
            <p className="text-foreground text-sm font-bold">"{search}"</p>
            <button onClick={() => { setSearch(''); setSearchParams({}); }} className="mt-3 text-xs text-primary hover:text-primary/80 font-bold cursor-pointer">Clear filters</button>
          </div>
        ) : (
          <div className={
            viewMode === 'large'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4'
          }>
            {filtered.map((item, i) => (
              <div key={item.id}
                className={`group relative overflow-hidden bg-card border border-border/40 transition-all duration-300 hover:shadow-soft-card hover:border-primary/15 opacity-0 animate-fade-in-up ${
                  viewMode === 'large' ? 'aspect-[4/3] rounded-3xl' : 'aspect-square rounded-2xl'
                }`}
                style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>

                {/* Media display */}
                {item.type === 'video' ? (
                  <div className="absolute inset-0">
                    <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted
                      onError={(e) => { e.target.poster = ''; }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform duration-200">
                        <Play size={20} className="text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => openLightbox(i)} className="absolute inset-0 cursor-pointer" aria-label={`View ${item.originalName || item.filename}`}>
                    <img src={item.url} alt={item.filename} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23fbf1f3" width="200" height="200"/><text fill="%23874e58" font-family="system-ui" font-size="12" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">No image</text></svg>'; }} />
                  </button>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                {/* Type badge */}
                {item.type === 'video' && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      <Video size={10} /> VIDEO
                    </span>
                  </div>
                )}

                {/* Category badge */}
                {catMap[item.categoryId] && activeCategory === 'all' && (
                  <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: catMap[item.categoryId].color }} />
                      {catMap[item.categoryId].name}
                    </span>
                  </div>
                )}

                {/* Bottom: filename + actions */}
                <div className="absolute inset-x-0 bottom-0 p-3 pt-10 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                  <p className="text-white text-xs font-bold truncate mb-2 drop-shadow-sm">{item.originalName || item.filename}</p>
                  <div className="flex items-center justify-between">
                    <button onClick={(e) => { e.stopPropagation(); handleDownload(item); }}
                      className="flex items-center gap-1 text-white/80 hover:text-white text-[11px] font-bold transition-colors cursor-pointer">
                      <Download size={12} /><span className="hidden sm:inline">Download</span>
                    </button>
                    <div className="relative" ref={shareMenu === item.id ? shareRef : undefined}>
                      <button onClick={(e) => { e.stopPropagation(); handleShare(item); }}
                        className="flex items-center gap-1 text-white/80 hover:text-white text-[11px] font-bold transition-colors cursor-pointer">
                        <Share2 size={12} /><span className="hidden sm:inline">Share</span>
                      </button>
                      {shareMenu === item.id && <ShareDropdown item={item} shareTo={shareTo} copyLink={copyLink} shareRef={shareRef} />}
                    </div>
                  </div>
                </div>

                {/* View icon */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  {item.type !== 'video' && (
                    <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                      <Eye size={16} className="text-white" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Lightbox ─── */}
      {lightbox.open && filtered.length > 0 && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/95 animate-fade-in" onClick={closeLightbox}
          role="dialog" aria-modal="true" aria-label="Media viewer"
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-white/40 text-sm font-mono tabular-nums">{lightbox.index + 1} / {filtered.length}</span>
              <span className="text-white/20">·</span>
              <span className="text-white/60 text-sm font-bold truncate max-w-[200px] sm:max-w-none">{filtered[lightbox.index].originalName || filtered[lightbox.index].filename}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={(e) => { e.stopPropagation(); handleDownload(filtered[lightbox.index]); }}
                className="p-2.5 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Download">
                <Download size={18} />
              </button>
              <div className="relative" ref={shareMenu === filtered[lightbox.index]?.id ? shareRef : undefined}>
                <button onClick={(e) => { e.stopPropagation(); handleShare(filtered[lightbox.index]); }}
                  className="p-2.5 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Share">
                  <Share2 size={18} />
                </button>
                {shareMenu === filtered[lightbox.index]?.id && <ShareDropdown item={filtered[lightbox.index]} shareTo={shareTo} copyLink={copyLink} shareRef={shareRef} />}
              </div>
              <button onClick={closeLightbox} className="p-2.5 text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer" aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative">
            {filtered.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-2 sm:left-4 p-3 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 cursor-pointer z-10 backdrop-blur-sm" aria-label="Previous">
                <ChevronLeft size={22} />
              </button>
            )}
            {filtered[lightbox.index].type === 'video' ? (
              <video src={filtered[lightbox.index].url} controls autoPlay
                className="max-w-full max-h-full rounded-xl select-none animate-scale-in" onClick={(e) => e.stopPropagation()} />
            ) : (
              <img src={filtered[lightbox.index].url} alt={filtered[lightbox.index].filename}
                className="max-w-full max-h-full object-contain rounded-xl select-none animate-scale-in" onClick={(e) => e.stopPropagation()} draggable={false} />
            )}
            {filtered.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-2 sm:right-4 p-3 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 cursor-pointer z-10 backdrop-blur-sm" aria-label="Next">
                <ChevronRight size={22} />
              </button>
            )}
          </div>
          <div className="px-4 py-3 text-center shrink-0">
            {filtered[lightbox.index].uploadedAt && (
              <p className="text-white/40 text-xs flex items-center justify-center gap-1.5 font-medium">
                <Calendar size={11} />
                {new Date(filtered[lightbox.index].uploadedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function ShareDropdown({ item, shareTo, copyLink, shareRef }) {
  return (
    <div className="absolute bottom-full right-0 mb-2 bg-card border border-border/50 rounded-2xl shadow-xl p-1.5 min-w-[170px] animate-scale-in z-50" ref={shareRef} onClick={(e) => e.stopPropagation()}>
      <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Share to</p>
      <button onClick={() => shareTo('whatsapp', item)} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer text-left">
        <MessageCircle size={15} className="text-green-600" /> WhatsApp
      </button>
      <button onClick={() => shareTo('twitter', item)} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer text-left">
        <ExternalLink size={15} className="text-sky-500" /> Twitter / X
      </button>
      <button onClick={() => shareTo('facebook', item)} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer text-left">
        <ExternalLink size={15} className="text-blue-600" /> Facebook
      </button>
      <div className="border-t border-border/50 my-1" />
      <button onClick={() => copyLink(item)} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-xl transition-colors cursor-pointer text-left">
        <Link size={15} className="text-muted-foreground" /> Copy Link
      </button>
    </div>
  );
}

export default PhotoGallery;
